import { useCallback, useEffect, useRef, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { isWhatsAppLinked } from "../../lib/whatsapp";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Loader2, RefreshCw } from "lucide-react";

const STATUS_POLL_MS = 3000;

// Pairs a WhatsApp account with GOWA: shows the QR code returned by
// POST /whatsapp/login, counts down its validity, and polls /whatsapp/status
// until the account is logged in (then hands the fresh status back).
export default function WhatsAppLoginDialog({ open, onOpenChange, onLinked }) {
  const [qr, setQr] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  const fetchQr = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError(null);
    setQr(null);
    try {
      const { data } = await api.post("/whatsapp/login");
      if (request !== requestRef.current) return;
      setQr(data.qr_image);
      setSecondsLeft(data.expires_in);
    } catch (e) {
      if (request !== requestRef.current) return;
      setError(errorMessage(e));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchQr();
    } else {
      requestRef.current += 1; // a late answer must not repopulate a closed dialog
      setQr(null);
      setError(null);
    }
  }, [open, fetchQr]);

  useEffect(() => {
    if (!qr || secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [qr, secondsLeft]);

  useEffect(() => {
    if (!open || !qr) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/whatsapp/status");
        if (!cancelled && isWhatsAppLinked(data)) onLinked(data);
      } catch {
        // Transient: the next tick tries again; the QR stays on screen.
      }
    }, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, qr, onLinked]);

  const expired = !!qr && secondsLeft <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="whatsapp-login-dialog">
        <DialogHeader>
          <DialogTitle>Connecter un compte WhatsApp</DialogTitle>
          <DialogDescription>
            Sur le téléphone du compte à utiliser : WhatsApp → Réglages → Appareils connectés → Connecter un appareil,
            puis scannez ce code.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3 py-2">
          <div className="relative flex items-center justify-center w-60 h-60 max-w-full rounded-lg border border-slate-200 bg-white">
            {loading ? (
              <Loader2 className="animate-spin text-slate-400" size={20} aria-label="Génération du QR code" />
            ) : error ? (
              <p className="px-4 text-center text-sm text-red-700" role="alert">{error}</p>
            ) : qr ? (
              <img
                src={qr}
                alt="QR code de connexion WhatsApp"
                className={`w-56 h-56 [image-rendering:pixelated] ${expired ? "opacity-20" : ""}`}
                data-testid="whatsapp-login-qr"
              />
            ) : null}
            {expired && (
              <span className="absolute text-sm font-medium text-slate-900">Code expiré</span>
            )}
          </div>
          {qr && !expired && (
            <p className="text-xs text-slate-500" aria-live="polite">
              Ce code expire dans {secondsLeft} s. La page se met à jour dès que le compte est connecté.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fermer</Button>
          {(expired || error) && (
            <Button onClick={fetchQr} disabled={loading}>
              <RefreshCw size={13} className="mr-1.5" />
              Nouveau code
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
